import { forwardRef } from 'react'
import { Typography } from '@mui/material'
import { type Theme, useTheme } from '@mui/material/styles'
import {
  type HighlightedPlace,
  IconSource,
  type Place,
  type RecentSearchPlace,
  type ShortItineraryIcon,
  type TransporterCode,
} from 'invictus-sdk-typescript'

import VisuallyHiddenLabel from '@DS/components/components/utils/aria/VisuallyHiddenLabel'
import { getDsIcon } from '@DS/components/icons/dsIcons'
import TransportIcon from '@DS/components/icons/transports/TransportIcon'
import { type AutocompleteItemProps } from '@DS/components/navigation/search/autocomplete/Autocomplete.types'

import * as styles from './styles'

// On ajoute explicitement la propriété `description` dans le type afin de la rendre optionnelle car elle n'est pas
// déclarée dans `RecentSearchPlace`
type RecentSearchPlaceExtended = RecentSearchPlace & {
  codes?: TransporterCode[]
}

export type AutocompleteResultModel = HighlightedPlace | RecentSearchPlaceExtended | Place

type AutocompleteResultProps = AutocompleteItemProps<AutocompleteResultModel>

const isPlaceModel = (model: AutocompleteResultModel): model is HighlightedPlace | RecentSearchPlaceExtended =>
  !!(model as Exclude<AutocompleteResultModel, Place>).type

// On évite que le background de l'icône se confonde avec le background de l'Autocomplete
const transformGeneratedBackground = (theme: Theme, lineIcon: ShortItineraryIcon): ShortItineraryIcon =>
  lineIcon.iconSource === IconSource.GENERATE &&
  lineIcon.generate &&
  ['FFF', 'FFFFFF', ''].includes(lineIcon.generate.color || '')
    ? {
        ...lineIcon,
        generate: { ...lineIcon.generate, color: (theme.customPalette.onElements.elevated || '').replace('#', '') },
      }
    : lineIcon

const AutocompleteResult = forwardRef<HTMLDivElement, AutocompleteResultProps>(({ model, isHighlighted }, ref) => {
  const theme = useTheme()

  const isPlace = isPlaceModel(model)

  if (!isPlace) {
    return null
  }

  const {
    label,
    type: { label: placeName, iconId },
    description,
    linesIcons,
  } = model
  const PlaceIcon = getDsIcon(iconId)

  return (
    <div ref={ref} css={styles.autocompleteResult(theme, isHighlighted)}>
      {PlaceIcon && <PlaceIcon css={[styles.autocompleteResultPlaceIconContainer, styles.placeIcon(theme)]} />}
      <div css={styles.autocompleteResultPlaceData}>
        <Typography css={styles.placeName} variant="caption" color="textSecondary">
          {placeName}
          <VisuallyHiddenLabel label=" : " />
        </Typography>
        <span>
          <Typography variant="body1" component="span">
            {label}
          </Typography>
          {description && (
            <Typography component="span" color="textSecondary">
              {` (${description})`}
            </Typography>
          )}
        </span>
        {linesIcons.length > 0 && (
          <span css={styles.autocompleteResultLinesContainer(theme)}>
            {linesIcons.map((lineIcon) => (
              <TransportIcon
                key={`${lineIcon.label}${lineIcon.name}${lineIcon.generate?.color || ''}`}
                {...transformGeneratedBackground(theme, lineIcon)}
                isSmallGenerated
              />
            ))}
          </span>
        )}
      </div>
    </div>
  )
})

export default AutocompleteResult
